import {DOCUMENT, Inject, Injectable, NgZone} from '@angular/core';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {CollectionMetadata} from '../models/clare';

export const DEFAULT_PRODUCT_PAGINATION_OPTIONS: SeFePaginationOptions = {
  name: 'Product pagination options',
  label: 'Products',
  currentPage: 1,
  total: 0,
  limit: 10,
  limits: [10, 20, 50],
};

interface PaginationOptionsConfig {
  defaultLimit?: number;
  label?: string;
  name?: string;
}

function positiveInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
}

export function buildPaginationOptions(
  metadata: CollectionMetadata | undefined,
  config: PaginationOptionsConfig = {}
): SeFePaginationOptions {
  const label = config.label ?? DEFAULT_PRODUCT_PAGINATION_OPTIONS.label;
  const defaultCurrentPage = DEFAULT_PRODUCT_PAGINATION_OPTIONS.currentPage ?? 1;
  const defaultLimit = config.defaultLimit ?? DEFAULT_PRODUCT_PAGINATION_OPTIONS.limit ?? 10;
  const defaultTotal = DEFAULT_PRODUCT_PAGINATION_OPTIONS.total ?? 0;
  const pagination = metadata?.pagination;

  return {
    ...DEFAULT_PRODUCT_PAGINATION_OPTIONS,
    name: config.name ?? `${label} pagination options`,
    label,
    currentPage: positiveInteger(pagination?.current_page, defaultCurrentPage),
    total: nonNegativeInteger(pagination?.total, defaultTotal),
    limit: positiveInteger(pagination?.limit, defaultLimit),
  };
}

/**
 * Scrolls the SE-FE list page back to the top after navigation events such as
 * search, pagination, or sort changes. Shared by the products tab and the
 * bundles tab so neither component needs to inject `DOCUMENT` or `NgZone`.
 */
@Injectable({providedIn: 'root'})
export class ListPageScrollService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {}

  scrollToTop(): void {
    this.ngZone.run(() => {
      const page = this.document.querySelector('.se-fe-page__wrapper');
      if (page) page.scrollTop = 0;
    });
  }
}
